import React, { useState, useRef } from "react";
import { TextField } from "@mui/material";
import {
  adminActions,
  authSelectors,
  useDispatch,
  useSelector,
} from "../../state";
import { Autocomplete } from "@mui/material";
import { useStyles } from "./AdminSearchBox.styles";
import { history } from "../../lib";
import { SearchIcon } from "../icons";

type AdminSearchBoxProps = {
  autoFocus?: boolean;
};

const AdminSearchBox = ({ autoFocus = false }: AdminSearchBoxProps) => {
  const classes = useStyles();
  const inputRef: any = useRef();
  const dispatch = useDispatch();
  const agencyId = useSelector(authSelectors.activeAgencyId);
  const activeRole = useSelector(authSelectors.activeRole);
  const [value, setValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [focused, setFocused] = useState(false);

  const handleSearchBoxChange = async (e, val, reason) => {
    if (e && reason !== "reset") {
      setValue(val);
    } else {
      setValue("");
      inputRef?.current?.blur();
      return;
    }

    if (!activeRole || !agencyId) {
      console.warn("active role and active agency id is not defined");
      return;
    }
    if (e?.target.value && val) {
      const results: any = await dispatch(
        adminActions.searchQuotes(e.target.value, activeRole, agencyId),
      );
      setSearchResults(results);
    } else {
      setValue("");
      setSearchResults([]);
    }
  };

  const onApplicationSelected = (e, val) => {
    setValue("");
    if (!val) {
      return;
    }
    history.push("/admin/quote-management/" + val.req_uid);
  };

  const isOptionEqualToValue = (option, value) => {
    return option.req_uid === value.req_uid;
  };

  return (
    <Autocomplete
      autoSelect={false}
      filterOptions={(x) => x}
      onChange={onApplicationSelected}
      className={classes.root}
      options={searchResults?.length ? searchResults : []}
      onInputChange={handleSearchBoxChange}
      popupIcon={<SearchIcon className={classes.popupIcon} />}
      forcePopupIcon
      // Open the popup on focus so the empty-state hint (`noOptionsText`)
      // surfaces immediately — the placeholder just reads "Search" and the
      // longer "Search quote by name, email, or phone" copy lives inside
      // the dropdown. Once the user starts typing we swap to "No results"
      // so the dropdown reflects the actual state of the query.
      //
      // `open` is controlled, so MUI requires an `onClose` to handle every
      // dismiss path it knows about (Escape key, outside-click, selecting
      // an option). Without it, those interactions can't update `focused`
      // and the popup gets stuck open until the input itself blurs.
      open={focused}
      onFocus={() => setFocused(true)}
      onClose={() => setFocused(false)}
      noOptionsText={
        value ? "No results" : "Search quote by name, email, or phone"
      }
      renderOption={(props, option) => {
        return (
          // `onMouseDown.preventDefault()` keeps the input focused while
          // the click is being processed. Without it, the mousedown blurs
          // the input first, which fires `onBlur` → `setSearchResults([])`
          // BEFORE the option's click → `onChange` runs. That race made
          // the option occasionally vanish before MUI could resolve the
          // selected value, causing intermittent "click does nothing"
          // reports on touch trackpads / certain mobile browsers.
          <li
            {...props}
            onMouseDown={(e) => e.preventDefault()}
            key={option.key}
          >
            {`${option.first_name} ${option.last_name} - ${option.email} - ${
              option.phone ?? ""
            } ${option.agency?.domain ? `- ${option.agency?.domain}` : ""}`}
          </li>
        );
      }}
      onBlur={() => {
        setFocused(false);
        setSearchResults([]);
      }}
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder="Search"
            size="small"
            autoFocus={autoFocus}
          />
        );
      }}
      inputValue={value}
    />
  );
};

export default AdminSearchBox;
